// Selling a package, and what the package screens say about one.
//
// **Adapters.** The rules are `core/src/package_sale.rs`: the sale's
// documents and its refusal, the expiry date, `Customize Charge`, what
// converts and the notice counting it, which lessons the forecast books, and
// the package screens' sentences that take a figure — so the Android and iOS
// package screens sell the same documents and say the same things.
// `core/src/family_money_vectors.json` holds the recorded answers. What
// MyMusicStaff does, and where this knowingly differs, is at the call sites
// (`usePackages.jsx`, `SellPackageWizard.jsx`, `useStudentPackageForecast.js`,
// `PackageActivityDrawer.jsx`, `StudentSidebar.jsx`,
// `PackageBalancesPanel.jsx`); `src/domain/packageTemplates.js` holds the
// template half.
//
// A figure a sentence quotes is handed in already written out where it is
// money (the studio's locale); package time is written by the core, whose
// `formatClock` that rendering is.

import { coreSync } from './core.js';
import { documentFromRows } from './documents.js';

const text = (v) => (v == null ? null : String(v));
const num = (v) => (v == null ? null : Number(v));

/**
 * A sale the rules would refuse, refused before anything is queued: the
 * core's code and sentence. Queued, a start date that is not a date and a
 * conversion for a student with no family would be two ledger rows
 * `validLedgerEntry` refuses, taking the purchase in the same batch down with
 * them after the wizard said "Package Added".
 */
export class PackageSaleRefused extends Error {
  constructor(refusal) {
    super(refusal.message);
    this.name = 'PackageSaleRefused';
    this.code = refusal.code;
  }
}

// ── The sale ───────────────────────────────────────────────────────────────

/** The wizard's three steps. */
export const saleSteps = () => [...coreSync().packageSaleSteps()];

/** `Apply Package Time`, MMS's two options and their values, default first. */
export const applyOptions = () => coreSync().packageApplyOptions();

/** A template's price as every screen reads it: never negative. */
export const templatePriceMinor = (t) => coreSync().packageTemplatePriceMinor(num(t?.priceMinor));

/**
 * What `Customize Charge` reveals: the template's price, as a box holds it —
 * a number; `''` with no template.
 */
export function salePrefillAmount(template, currency) {
  return template ? Number(coreSync().salePrefillAmount(num(template.priceMinor), currency)) : '';
}

/**
 * The override `Customize Charge` asks for, in minor units, or `null` when
 * it is off or empty — which means "the template's price".
 */
export function saleChargeMinor({ customize, amount }, currency) {
  return coreSync().saleChargeMinor(Boolean(customize), String(amount), currency) ?? null;
}

/** Why the override is refused, or null; only asked while it is on (#173). */
export function saleAmountProblem({ customize, amount }, currency) {
  return coreSync().saleAmountProblem(Boolean(customize), String(amount ?? ''), currency)?.message ?? null;
}

/** Whether step one's Next is live: a template and a student. */
export const saleCanContinue = ({ template, studentId }) => (
  coreSync().saleCanContinue(Boolean(template), text(studentId))
);

/** The price the review shows and the sale charges. */
export const salePriceMinor = (chargeMinor, template) => (
  coreSync().salePriceMinor(num(chargeMinor), num(template?.priceMinor))
);

/**
 * What `Existing events` converts: the convertible lessons on or after the
 * package start date. `New events only` converts none.
 */
export function convertingLessons(convertible, apply, startOn) {
  const order = coreSync().convertingLessons(convertible.map((l) => String(l.date)), String(apply), String(startOn));
  return [...order].map((i) => convertible[i]);
}

/**
 * The line under the radio, counting what it will convert; null for none.
 * `datePattern` is the studio's (`useDatePattern()`): the core draws the
 * start date in it (#510), as every date in this module's sentences.
 */
export const convertNotice = (count, startOn, datePattern) => (
  coreSync().convertNotice(count, String(startOn), String(datePattern)) ?? null
);

/**
 * `Days to Expire After Start Date` resolved against the sale's start date,
 * on the UTC grid; null for no expiry or an unreadable start.
 */
export const expiryDateOf = (startOn, days) => coreSync().packageExpiryDate(text(startOn), num(days)) ?? null;

// A sale as the core's `PackageSale`, and its lessons as `ConvertLesson`s.
function saleIn({
  pkgId, uid, studentId, familyId, template, startOn, chargeMinor = null, note = '', teacherName = '',
}) {
  return {
    pkgId: String(pkgId),
    uid: String(uid ?? ''),
    studentId: String(studentId ?? ''),
    familyId: text(familyId),
    templateId: text(template?.id),
    templateName: text(template?.name),
    templateMinutes: num(template?.minutes),
    templatePriceMinor: num(template?.priceMinor),
    templateExpiryDays: num(template?.expiryDays),
    startOn: String(startOn ?? ''),
    chargeMinor: num(chargeMinor),
    note: text(note),
    teacherName: text(teacherName),
  };
}

const convertIn = (convert = []) => convert.map((l) => ({
  occurrenceId: String(l.occurrenceId),
  date: String(l.date),
  minutes: num(l.minutes),
  priceMinor: num(l.priceMinor),
  description: text(l.description),
}));

/**
 * The documents one sale writes, in batch order: the purchase; the package
 * charge on the family account when there is a family and a price; and for
 * each converted lesson the consume and its charge tombstoned (both merging).
 * `pkgId` is the caller's random id; `uid` is the teacher's. Throws
 * `PackageSaleRefused` for a sale the rules would refuse.
 */
export function packageSaleDocuments(input, serverTimestamp) {
  const sale = saleIn(input);
  const convert = convertIn(input.convert);
  const refusal = coreSync().packageSaleRefusal(sale, convert);
  if (refusal) throw new PackageSaleRefused(refusal);
  return coreSync().packageSaleWrites(sale, convert).map((w, i) => ({
    collection: w.collection,
    id: w.id,
    merge: w.merge,
    data: documentFromRows(coreSync().packageSaleDocument(sale, convert, i), serverTimestamp),
  }));
}

// ── Which lessons the forecast and the conversion see ──────────────────────

/**
 * How far a forecast looks, in days — `ATTENDANCE_DAYS_FORWARD`'s twin, and a
 * literal because the tests read it at module scope; pinned to the core's by
 * test/domain/familyMoneyCore.test.js.
 */
export const PACKAGE_FORECAST_DAYS_FORWARD = 90;

/** The horizon a forecast looked to, from a day. */
export const forecastHorizon = (today) => coreSync().packageForecastHorizon(today);

// A history as the core's `PackageHistoryLesson`s, each with its price.
const historyIn = (history, student, priceFor) => history.map((o) => {
  const price = priceFor(o, student);
  return {
    id: String(o.id),
    date: String(o.date ?? ''),
    status: String(o.status ?? ''),
    durationMin: num(o.durationMin),
    pricingRule: text(o.pricingRule),
    requiresMakeupCredit: o.requiresMakeupCredit === true,
    description: text(o.description),
    priceMinor: num(price),
  };
});

/**
 * The unrecorded lessons in a history, and each as a `BookedLesson` —
 * see `useStudentPackageForecast.js` for which lessons are handed to the fold
 * and why. `priceFor(o, student)` is `useEvents`'.
 */
export function bookedLessonsOf({ history, student, priceFor }) {
  const booked = coreSync().bookedPackageLessons(historyIn(history, student, priceFor));
  const byId = new Map(history.map((o) => [String(o.id), o]));
  return { lessons: booked.map((b) => byId.get(b.id)), booked };
}

/**
 * What `Apply Package Time → Existing events…` will convert, for one
 * student: marked, billable, on the student's own pricing and priced above
 * zero. The start-date bound is `convertingLessons`'.
 */
export function convertibleLessonsOf({ history, student, priceFor }) {
  return coreSync().convertiblePackageLessons(historyIn(history, student, priceFor)).map((l) => ({
    occurrenceId: l.occurrenceId,
    date: l.date,
    minutes: l.minutes,
    priceMinor: l.priceMinor,
    description: l.description,
  }));
}

// ── Package Balances ───────────────────────────────────────────────────────

/** The Total Time column: every package's purchased minutes. */
export const packagesTotalMinutes = (forecast) => coreSync().packagesTotalMinutes(
  forecast.packages.map((p) => Number(p.purchased)),
);

/** The count under the table of students with no row, or null for none. */
export const unlistedStudentsText = (n) => coreSync().unlistedStudentsText(n) ?? null;

// ── Package Activity ───────────────────────────────────────────────────────

/**
 * The Activity cell of an entry's row. A purchase names its template and its
 * price (`priceText`, the money written out) when it has them.
 */
export const activityDescription = (entry, priceText) => coreSync().packageActivityDescription(
  String(entry.kind ?? ''), text(entry.templateName), num(entry.priceMinor), String(priceText ?? ''),
);

/** The Time cell of an entry's row: `+` for time in, `−` for time out. */
export const activityDelta = (entry) => coreSync().packageActivityDelta(String(entry.kind ?? ''), num(entry.minutes));

/** The Activity cell of a projected lesson. */
export const bookedRowLabel = (row) => coreSync().packageBookedRowLabel(Boolean(row.behind), Boolean(row.uncovered));

/** The Time cell of a projected lesson. */
export const bookedRowTime = (row, minutes) => coreSync().packageBookedRowTime(Boolean(row.uncovered), Number(minutes));

/** How a package is named in the Package column: its template, or its sale. */
export function packageName(purchase, datePattern) {
  if (!purchase) return '';
  return coreSync().packageName(text(purchase.templateName), String(purchase.occurredOn), String(datePattern));
}

/** The Package cell: the packages a row drew on, joined. */
export const packageNamesText = (names) => coreSync().packageNamesText(names.map(String));

/** Whether the log carries a Package column: only for two or more. */
export const showsPackageColumn = (forecast) => coreSync().showsPackageColumn(forecast?.packages?.length ?? 0);

/** The sentence over the log when the time runs out, or null. */
export const runsOutText = (forecast, datePattern) => (
  coreSync().packageRunsOutText(text(forecast?.runsOutOn), String(datePattern)) ?? null
);

/** The covered case's sentence, naming how far it looked, or null. */
export function coveredThroughText(forecast, datePattern) {
  if (!forecast) return null;
  return coreSync().packageCoveredThroughText(text(forecast.runsOutOn), text(forecast.horizon), String(datePattern)) ?? null;
}

/** The unmarked-past-lessons sentence, or null. */
export const unmarkedBehindText = (forecast) => (
  coreSync().packageUnmarkedBehindText(Number(forecast?.unmarkedBehind ?? 0)) ?? null
);

/** What the expiring packages' dates mean, or null when none is dated. */
export const expiringText = (dates, datePattern) => (
  coreSync().packageExpiringText(dates.map(String), String(datePattern)) ?? null
);

/** Time already lost to an expiry, or null. */
export const forfeitedText = (forecast) => coreSync().packageForfeitedText(Number(forecast?.forfeited ?? 0)) ?? null;

// ── The student rail ───────────────────────────────────────────────────────

// A forecast's package as the core's `PackageState`.
const stateIn = (p) => ({
  id: String(p?.id ?? ''),
  purchased: Math.round(Number(p?.purchased) || 0),
  remaining: Math.round(Number(p?.remaining) || 0),
  expiresOn: text(p?.expiresOn) || null,
  forfeited: Math.round(Number(p?.forfeited) || 0),
  lapsed: Boolean(p?.lapsed),
});

/**
 * The next package to lapse that still holds time — the first in draw order
 * with a date, not lapsed, and something left — or null.
 */
export function nextExpiringPackage(packages) {
  const i = coreSync().nextExpiringPackage(packages.map(stateIn));
  return i == null ? null : packages[i];
}

/**
 * The rail's `Package Time` block, as lines: `lead`, the figure in bold
 * (`value`), and `tail`; `note` marks the smaller print under the two
 * figures MMS shows. No purchase is one line with no figure; a caller with no
 * forecast gets the balance alone.
 */
export function packageTimeLines(summary, forecast, datePattern) {
  return coreSync().packageTimeLines(
    Number(summary.purchased),
    Number(summary.remaining),
    Boolean(forecast),
    Number(forecast?.upcoming ?? 0),
    Number(forecast?.available ?? 0),
    Number(forecast?.unmarkedBehind ?? 0),
    Number(forecast?.forfeited ?? 0),
    (forecast?.packages || []).map(stateIn),
    String(datePattern),
  );
}
